// Supabase client with environment-based configuration
// Supports separate development and production instances
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// SECURITY: read environment variables ONLY as static `import.meta.env.VITE_X`
// property accesses — never `import.meta.env` as an object (no aliasing,
// spreading, enumerating its keys, or passing it around).
//
// Vite replaces each static access with just that one value at build time. Any
// use of the object as a whole forces it to inline EVERY VITE_* variable present
// in the build environment into the public JS bundle. That is how
// VITE_SENTRY_AUTH_TOKEN ended up readable by anyone on cartxray.com: this file
// used to scan the whole env for a key-name prefix. vite.config.ts now fails the
// build if a secret value appears in the output, and
// src/integrations/supabase/client.env.test.ts guards this file.

// Determine if we're in development mode
const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development'
  || window.location.hostname === 'localhost'
  || window.location.hostname === '127.0.0.1';

// Supabase URLs (dev uses optional override, production relies on Vercel env)
const DEV_URL = import.meta.env.VITE_SUPABASE_URL_DEV;
const PRODUCTION_URL = import.meta.env.VITE_SUPABASE_URL;

const SUPABASE_URL = (isDevelopment && DEV_URL ? DEV_URL : PRODUCTION_URL) ?? '';

// Discover the publishable key depending on environment
const resolvePublishableKey = (): string | undefined => {
  // Priority 1: New-format publishable key (sb_publishable_…), under an explicit
  // name. (This used to be discovered by scanning every env var for a
  // `VITE_sb_publishable_` prefix — the scan is what leaked the whole env.)
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (publishableKey) {
    if (import.meta.env.DEV) console.log('[Supabase] Using new-format publishable key');
    return publishableKey;
  }

  // Priority 2: Development legacy key
  if (isDevelopment && import.meta.env.VITE_SUPABASE_ANON_KEY_DEV) {
    if (import.meta.env.DEV) console.warn('[Supabase] Using legacy VITE_SUPABASE_ANON_KEY_DEV. Migrate to VITE_SUPABASE_PUBLISHABLE_KEY.');
    return import.meta.env.VITE_SUPABASE_ANON_KEY_DEV;
  }

  // Priority 3: Production legacy key
  if (import.meta.env.VITE_SUPABASE_ANON_KEY) {
    if (import.meta.env.DEV) console.warn('[Supabase] Using legacy VITE_SUPABASE_ANON_KEY. Migrate to VITE_SUPABASE_PUBLISHABLE_KEY.');
    return import.meta.env.VITE_SUPABASE_ANON_KEY;
  }

  return undefined;
};

const SUPABASE_KEY = resolvePublishableKey() ?? '';

if (!SUPABASE_URL) {
  throw new Error('[Supabase] Missing Supabase URL. Set VITE_SUPABASE_URL (and optionally VITE_SUPABASE_URL_DEV for development).');
}

if (!SUPABASE_KEY) {
  const message = isDevelopment
    ? '[Supabase] Missing development key. Set VITE_SUPABASE_PUBLISHABLE_KEY (preferred) or VITE_SUPABASE_ANON_KEY_DEV (legacy) in your .env.local file.'
    : '[Supabase] Missing publishable key. Set VITE_SUPABASE_PUBLISHABLE_KEY (preferred) or VITE_SUPABASE_ANON_KEY (legacy) in your environment variables.';
  throw new Error(message);
}

if (import.meta.env.DEV) {
  console.log(`[Supabase] Using ${isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'} instance`);
  console.log(`[Supabase] URL: ${SUPABASE_URL}`);
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  }
});
